import { Building2, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { CardsSection } from 'components/layout/Section/CardsSection';
import { NsKey } from 'enums/NsKey';
import { sirenOrSiretLabel } from 'shared/utils/sirenSiret';

import { PersonneMoraleProfileData } from './personneMoraleProfile';

interface PersonneMoraleInfoCardProps {
  profile: PersonneMoraleProfileData;
  variant: 'identity' | 'contact';
  /** Slot on the right of the title (edit button on the governance side). None = read-only. */
  titleAside?: React.ReactNode;
}

const InfoRow: React.FC<{ label: string; value?: React.ReactNode }> = ({
  label,
  value
}) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
      {label}
    </span>
    <span className="text-sm text-gray-900">{value || '—'}</span>
  </div>
);

const formatCurrency = (amount?: number | null) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
  }).format(amount ?? 0);

/**
 * Read-only Identity / Contact cards of a personne morale — the single render
 * shared by the Gouvernance tab (which plugs its edit button), the full PM
 * details page and the intermediary quick view. Fields specific to one context
 * (company code, corporate email, detail-only fields) only show when present.
 */
export const PersonneMoraleInfoCard: React.FC<PersonneMoraleInfoCardProps> = ({
  profile,
  variant,
  titleAside
}) => {
  const { t } = useTranslation(NsKey.PersonneMorale);

  if (variant === 'identity') {
    return (
      <CardsSection
        icon={<Building2 className="w-5 h-5 text-primary" />}
        title={t('identity.title')}
        titleAside={titleAside}
      >
        <p className="-mt-2 text-sm text-gray-500">{t('identity.subtitle')}</p>
        <div className="rounded-2xl border bg-white p-6">
          <div className="grid grid-cols-2 gap-x-8 gap-y-4 md:grid-cols-3">
            <InfoRow label={t('identity.name')} value={profile.name} />
            {profile.companyCode != null && (
              <InfoRow
                label={t('identity.companyCode')}
                value={profile.companyCode}
              />
            )}
            <InfoRow
              label={sirenOrSiretLabel(profile.siren ?? '')}
              value={profile.siren}
            />
            <InfoRow
              label={t('identity.capital')}
              value={formatCurrency(profile.capital)}
            />
            <InfoRow label={t('identity.rcsCity')} value={profile.rcsCity} />
            <InfoRow
              label={t('identity.rcsNumber')}
              value={profile.rcsNumber}
            />
            <InfoRow label={t('identity.orias')} value={profile.orias} />
            <InfoRow label={t('identity.rcp')} value={profile.rcp} />
            {profile.oriasLastControl != null && (
              <InfoRow
                label={t('identity.oriasLastControl')}
                value={new Date(profile.oriasLastControl).toLocaleDateString()}
              />
            )}
            {profile.acquisitionChannel != null &&
              profile.acquisitionChannel !== 0 && (
                <InfoRow
                  label={t('identity.acquisitionChannel')}
                  value={t(
                    `identity.acquisitionChannels.${profile.acquisitionChannel}`,
                    { defaultValue: '—' }
                  )}
                />
              )}
            {profile.attachedBroker != null && (
              <InfoRow
                label={t('identity.attachedBroker')}
                value={
                  profile.attachedBroker ? t('identity.yes') : t('identity.no')
                }
              />
            )}
            {profile.comment && (
              <div className="col-span-2 md:col-span-3">
                <InfoRow
                  label={t('identity.comment')}
                  value={profile.comment}
                />
              </div>
            )}
          </div>
        </div>
      </CardsSection>
    );
  }

  return (
    <CardsSection
      icon={<MapPin className="w-5 h-5 text-primary" />}
      title={t('contact.title')}
      titleAside={titleAside}
    >
      <p className="-mt-2 text-sm text-gray-500">{t('contact.subtitle')}</p>
      <div className="rounded-2xl border bg-white p-6">
        <div className="grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-3">
          <div className="md:col-span-3">
            <InfoRow
              label={t('contact.address')}
              value={profile.addressInline}
            />
          </div>
          <InfoRow label={t('contact.phone')} value={profile.phone} />
          {profile.email != null && (
            <InfoRow
              label={t(profile.emailLabelKey ?? 'contact.email')}
              value={profile.email}
            />
          )}
        </div>
      </div>
    </CardsSection>
  );
};
